import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Button } from 'antd';
import { CloseOutlined, ExclamationCircleFilled, ToolFilled, WarningFilled } from '@ant-design/icons';
import { maintenanceWindow, STATUS_PAGE_URL, statusEvents, useSystemStatus } from './useSystemStatus';
import './SystemStatus.scss';

// App-wide bar (top right; bottom on phones): an incident, maintenance in progress, or
// maintenance planned in the next days. Planned maintenance can be closed (remembered per
// maintenance in this browser); incidents and maintenance in progress stay until they end.
// Not shown on the dashboard, which has the full card.
const DISMISS_KEY = '2way.dismissedMaintenance';
const HIDDEN_ON = ['/my-dashboard'];

const readDismissed = () => {
    try {
        return JSON.parse(window.localStorage.getItem(DISMISS_KEY) || '[]');
    } catch (e) {
        return [];
    }
};

const SystemStatusBar = ({intl, location}) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const status = useSystemStatus();
    const [dismissed, setDismissed] = useState(readDismissed);
    if (!status || HIDDEN_ON.includes(location.pathname)) return null;

    const {incidents, ongoing, planned} = statusEvents(status);
    const incident = incidents.find(item => item.state === 'downtime') || incidents[0];
    const now = ongoing[0];
    const next = planned.find(item => !dismissed.includes(item.id));
    if (!incident && !now && !next) return null;

    let tone = 'maint';
    let icon = <ToolFilled/>;
    let text;
    if (incident) {
        tone = incident.state === 'downtime' ? 'down' : 'warn';
        icon = tone === 'down' ? <ExclamationCircleFilled/> : <WarningFilled/>;
        const at = moment(incident.lastUpdate?.at || incident.startedAt).format('HH:mm');
        text = <><b>{incident.title}</b> · {t('systemStatus.lastUpdate', 'last update {time}', {time: at})}</>;
    } else if (now) {
        text = (
            <>
                <b>{now.endsAt
                    ? t('systemStatus.maintenanceUntil', 'Maintenance in progress until {time}', {time: moment(now.endsAt).format('HH:mm')})
                    : t('systemStatus.maintenance', 'Maintenance in progress')}</b>
                {now.message ? <> · {now.message}</> : null}
            </>
        );
    } else {
        text = (
            <>
                <b>{t('systemStatus.plannedMaintenance', 'Planned maintenance')}: {maintenanceWindow(next)}</b>
                {next.message ? <> · {next.message}</> : null}
            </>
        );
    }

    const close = () => {
        const list = [...dismissed, next.id];
        setDismissed(list);
        try {
            window.localStorage.setItem(DISMISS_KEY, JSON.stringify(list.slice(-20)));
        } catch (e) {
            // storage unavailable: closed for this visit only
        }
    };

    return (
        <div className={`system-status-bar system-status-bar--${tone}`} role="status">
            <span className="system-status-bar__icon">{icon}</span>
            <span className="system-status-bar__text">{text}</span>
            <a href={status.pageUrl || STATUS_PAGE_URL} target="_blank" rel="noopener noreferrer" className="system-status-bar__link">
                {t('systemStatus.details', 'Details')}
            </a>
            {!incident && !now ? (
                <Button size="small" type="text" icon={<CloseOutlined/>} onClick={close} aria-label={t('systemStatus.close', 'Close')}/>
            ) : null}
        </div>
    );
};

export default withRouter(injectIntl(SystemStatusBar));
